import { Fragment, type ReactNode } from 'react'
import { Link } from 'react-router'
import { ArrowDownRight, ArrowUpRight, ChevronRight, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Tooltip } from './overlay'

export function PageHeader({
  title,
  description,
  actions,
  breadcrumbs,
  meta,
}: {
  title: ReactNode
  description?: ReactNode
  actions?: ReactNode
  breadcrumbs?: { label: string; to?: string }[]
  meta?: ReactNode
}) {
  return (
    <div className="mb-6">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="mb-2 flex items-center gap-1 text-[13px] text-ink-500">
          {breadcrumbs.map((b, i) => (
            <Fragment key={i}>
              {i > 0 && <ChevronRight className="h-3.5 w-3.5 text-ink-300" aria-hidden />}
              {b.to ? (
                <Link to={b.to} className="hover:text-ink-800">
                  {b.label}
                </Link>
              ) : (
                <span aria-current="page" className="text-ink-700">
                  {b.label}
                </span>
              )}
            </Fragment>
          ))}
        </nav>
      )}
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-[26px] leading-tight font-semibold tracking-tight text-ink-950">{title}</h1>
          {description && <p className="mt-1 max-w-3xl text-sm text-ink-500">{description}</p>}
          {meta && <div className="mt-2 flex flex-wrap items-center gap-2">{meta}</div>}
        </div>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </div>
  )
}

/** KPI tile: value, definition tooltip and change vs. the previous equal-length period. */
export function KpiCard({
  label,
  value,
  previous,
  change,
  definition,
  icon,
  goodWhen = 'up',
  footer,
  className,
}: {
  label: string
  value: ReactNode
  previous?: ReactNode
  change?: number | null
  definition?: string
  icon?: ReactNode
  goodWhen?: 'up' | 'down' | 'neutral'
  footer?: ReactNode
  className?: string
}) {
  const dir = change === null || change === undefined ? null : change > 0 ? 'up' : change < 0 ? 'down' : 'flat'
  const good = goodWhen === 'neutral' || dir === 'flat' ? null : dir === goodWhen
  const body = (
    <div className={cn('card flex flex-col p-4', className)}>
      <div className="flex items-center justify-between gap-2">
        <p className="text-[13px] font-medium text-ink-500">{label}</p>
        {icon && <span className="text-ink-300">{icon}</span>}
      </div>
      <p className="mt-2 font-display text-[26px] leading-none font-semibold tracking-tight text-ink-950">{value}</p>
      <div className="mt-2.5 flex min-h-[20px] items-center gap-2 text-xs">
        {dir && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 font-semibold tabular',
              good === null ? 'bg-ink-100 text-ink-600' : good ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-800',
            )}
          >
            {dir === 'up' ? <ArrowUpRight className="h-3 w-3" /> : dir === 'down' ? <ArrowDownRight className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
            {Math.abs(change!).toFixed(1)}%
          </span>
        )}
        {previous !== undefined && previous !== null && <span className="text-ink-500">vs {previous} prev.</span>}
        {footer}
      </div>
    </div>
  )
  return definition ? (
    <Tooltip content={definition}>
      <div tabIndex={0} aria-label={`${label}: ${definition}`} className="rounded-[var(--radius-card)]">
        {body}
      </div>
    </Tooltip>
  ) : (
    body
  )
}

export function Section({ title, description, actions, children, className }: { title: ReactNode; description?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <section className={cn('mb-8', className)}>
      <div className="mb-3 flex items-end justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-ink-900">{title}</h2>
          {description && <p className="mt-0.5 text-[13px] text-ink-500">{description}</p>}
        </div>
        {actions}
      </div>
      {children}
    </section>
  )
}
